/* POHYB — jedna škála dob a křivek pro nový a přestavovaný kód (DESIGN-PRAVIDLA §8).
 * ============================================================================
 * Zrcadlí CSS tokeny `--duration-*` / `--ease-smooth-out` z index.css.
 * Pravidla (emil-design-eng): krátké, bez překmitu, jen transform/opacity,
 * jedna orchestrovaná sekvence při načtení, nic se neopakuje při přepočtu.
 *
 * Omezený pohyb: helpery dostanou `bezPohybu` (z `useReducedMotion()` z motion/react
 * nebo z `useBezPohybu()` níž) a vrátí stav, kde je VŠE hned vidět —
 * `initial: false`, žádná opacity 0, žádný posun.
 */
import { useReducedMotion } from "motion/react";

/** Doby v sekundách (motion/react bere sekundy). */
export const MIKRO = 0.08; // stisk
export const RYCHLE = 0.15; // hover, tooltip, čip
export const UI = 0.25; // panel, záložka, okno
export const VSTUP = 0.35; // načtení stránky
export const KROK = 0.04; // stagger mezi prvky
export const MAX_KROKU = 6; // stagger jen u prvních 6 prvků, další přijdou s 6.

/** Křivky: příchod a odchod (odchod je o třetinu kratší). */
export const EASE_OUT = [0.22, 1, 0.36, 1];
export const EASE_ODCHOD = [0.4, 0, 1, 1];
export const odchodDoba = (doba) => Math.round(doba * (2 / 3) * 1000) / 1000;

/** Systémové „omezit pohyb“ bez hooku (pro mimo-React a první render). */
export function systemBezPohybu() {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function"
      && !!window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Hook: true = omezený pohyb (motion/react + přímý dotaz na systém). */
export function useBezPohybu() {
  const reduce = useReducedMotion();
  return !!reduce || systemBezPohybu();
}

/**
 * Props pro vstup jednoho prvku (fade + posun o `posun` px).
 * @param {boolean} bezPohybu
 * @param {{ zpozdeni?: number, posun?: number, doba?: number }} [moznosti]
 * @returns {object} props pro <motion.div>
 */
export function vstup(bezPohybu, { zpozdeni = 0, posun = 4, doba = VSTUP } = {}) {
  if (bezPohybu) return { initial: false, animate: { opacity: 1, y: 0 } };
  return {
    initial: { opacity: 0, y: posun },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: posun, transition: { duration: odchodDoba(doba), ease: EASE_ODCHOD } },
    transition: { duration: doba, ease: EASE_OUT, delay: zpozdeni },
  };
}

/**
 * Props pro i-tý prvek sekvence (stagger KROK, nejvýš MAX_KROKU kroků).
 * @param {boolean} bezPohybu
 * @param {number} index
 * @param {{ start?: number, posun?: number, doba?: number }} [moznosti]
 */
export function vstupPoradi(bezPohybu, index, { start = 0, posun = 4, doba = VSTUP } = {}) {
  const i = Number.isFinite(index) && index > 0 ? Math.min(index, MAX_KROKU) : 0;
  return vstup(bezPohybu, { zpozdeni: start + i * KROK, posun, doba });
}

/** Přechod pro hover/čip/tooltip (RYCHLE) — při omezeném pohybu okamžitý. */
export function prechod(bezPohybu, doba = RYCHLE) {
  return bezPohybu ? { duration: 0 } : { duration: doba, ease: EASE_OUT };
}

/**
 * Nájezd „z nuly“ (čísla, čára trendu) jen při PRVNÍM otevření v relaci
 * prohlížeče (sessionStorage) — obrazovky jako nástěnka se otevírají desítkykrát
 * denně a pokaždé od nuly by zdržovaly (pilot 29. 9. 2026: „vstup grafů 1× za
 * relaci“). Bez úložiště (soukromé okno) raději bez nájezdu než nájezd pokaždé.
 * @param {string} klic  např. "bpu.nastenka.nabeh"
 * @returns {boolean} true = poprvé v této relaci
 */
export function prvniVRelaci(klic) {
  try {
    if (sessionStorage.getItem(klic)) return false;
    sessionStorage.setItem(klic, "1");
    return true;
  } catch {
    return false;
  }
}
